import {
    IPersonalizationData,
    IPersonalizationExpression,
    IPersonalizationItem, ExpressionsOperator,
    PersonalizationFieldType,
    PersonalizationMap,
} from '../personalization-interfaces';
import { getJSONFromHtml, isNumeric } from '../../../domain/common';

export const PERSONALIZATION_PLACEHOLDER_ID = 'prs-data';

const getTypeByValue = (value: unknown) : PersonalizationFieldType => {
    if (value === null || value === undefined) return PersonalizationFieldType.Null;

    switch (typeof value) {
        case 'string':
            return PersonalizationFieldType.String;
        case 'number':
            return PersonalizationFieldType.Number;
        case 'boolean':
            return PersonalizationFieldType.Boolean;
        default:
            return PersonalizationFieldType.Other;
    }
};

export const preparePersonalizationData = (data: IPersonalizationData) : PersonalizationMap => {

    const map: PersonalizationMap = new Map();

    for(const [key, value] of Object.entries(data)) {

        const property = key.toLowerCase();

        map.set(property, {
            name: property,
            type: getTypeByValue(value),
            value,
        });
    }

    return map;
};

const isFallbackVisible = ($node: HTMLElement) : boolean => {
    if(!$node) return false;

    const visible = !$node.classList.contains('hidden');
    if(!visible) return false;

    const $directChildren = $node.querySelectorAll(':scope > [data-user-prop]');
    for(const $child of $directChildren) {
        if(isFallbackVisible($child as HTMLElement)) return true;
    }

    return $directChildren.length === 0;
};

const ifExpressionsTrue = (map: PersonalizationMap, expression: IPersonalizationExpression) : boolean => {
    const itemData = map.get(expression.field.toLowerCase());
    if(!itemData) return false;

    switch(expression.operator) {
        case '=': {
            return itemData.value === expression.value;
        }
        case '!=': {
            return itemData.value !== expression.value;
        }
        case '>': {
            return itemData.value > (expression.value as number | string);
        }
        case '>=': {
            return itemData.value >= (expression.value as number | string);
        }
        case '<': {
            return itemData.value < (expression.value as number | string);
        }
        case '<=': {
            return itemData.value <= (expression.value as number | string);
        }
    }

    return false;
};

const ifItemShouldBeShown = (map: PersonalizationMap, item: IPersonalizationItem) : boolean => {

    const expressions = item.expressions || [];
    if(expressions.length <= 0) return false;

    switch (item.operator) {
        case ExpressionsOperator.And: {

            let shouldBeShown = true;
            for(const expr of expressions) {
                if(!ifExpressionsTrue(map, expr)) {
                    shouldBeShown = false;
                    break;
                }
            }
            return shouldBeShown;
        }
        case ExpressionsOperator.Or: {
            let shouldBeShown = false;
            for(const expr of expressions) {
                if(ifExpressionsTrue(map, expr)) {
                    shouldBeShown = true;
                    break;
                }
            }
            return shouldBeShown;
        }
        default: {
            // In case we don't have ' and ', ' or '
            // we assume that we have only one expression.
            return ifExpressionsTrue(map, expressions[0]);
        }
    }
};

const parseExpression = (expression: string) : IPersonalizationExpression|null => {

    const pattern = /^\s*(.+?)\s*(>=|<=|!=|>|<|=)\s*(.+?)\s*$/;
    const normalized = expression.toString().trim();
    const match = normalized.match(pattern);
    if(!match) return null;

    const [, left, center, right] = match;

    const operator = center.trim();
    if(operator === '') return null;

    const field = left.trim();
    const value = right.trim();
    const loweredValue = value.toLowerCase();

    let itemValue: string|number|boolean|null = null;
    let itemType: PersonalizationFieldType = PersonalizationFieldType.Null;

    if(loweredValue === 'true' || loweredValue === 'false'){
        itemValue = loweredValue === 'true';
        itemType = PersonalizationFieldType.Boolean;
    }
    else{
        const isNum = isNumeric(loweredValue);
        itemValue = isNum ? Number(loweredValue) : value;
        itemType = isNum ? PersonalizationFieldType.Number : PersonalizationFieldType.String;
    }

    return {
        expression,
        field,
        operator,
        value: itemValue,
        type: itemType,
    };
};

export const findPersonalizationItems = () : IPersonalizationItem[] => {
    const personalizationItems: IPersonalizationItem[] = [];

    const $elements = document.querySelectorAll('[data-user-prop]');
    for(const $element of $elements) {

        const prop = $element.getAttribute('data-user-prop');
        if(!prop) continue;

        const lower = prop.toLowerCase();
        const hasAndOr = lower.includes(' and ') || lower.includes(' or ');
        if(!hasAndOr) {
            const expression = parseExpression(prop);
            if(!expression) continue;

            personalizationItems.push({
                $element: $element as HTMLElement,
                operator: ExpressionsOperator.None,
                expressions: [
                    expression
                ]
            });

            continue;
        }

        const expressions: IPersonalizationExpression[] = [];
        const parts = prop.split(/(\s+(?:and|or)\s+)/i);
        for(const part of parts) {
            const expression = parseExpression(part);
            if(!expression) continue;

            expressions.push(expression);
        }

        if(expressions.length <= 0) continue;

        personalizationItems.push({
            $element: $element as HTMLElement,
            operator: lower.includes(' and ') ? ExpressionsOperator.And : ExpressionsOperator.Or,
            expressions,
        });

    }

    return personalizationItems;
};

export const getPersonalizationProperty = (property: string) : string|number|boolean|null => {
    const data: IPersonalizationData|null = getJSONFromHtml(document.getElementById(PERSONALIZATION_PLACEHOLDER_ID)) as IPersonalizationData;
    if(!data) return null;

    return data[property];
};

export const showPersonalizationItems = (map: PersonalizationMap, items: IPersonalizationItem[]) => {

    for(const item of items) {

        const isShow = ifItemShouldBeShown(map, item)
        if(isShow) {
            item.$element.classList.remove('hidden');
        }
    }
};

export const handleGroupFallbacks = () => {
    const $groups = document.querySelectorAll('[data-user-banner]');

    for(const $group of $groups) {
        const $fallback = $group.querySelector('[data-user-default]');
        if(!$fallback) continue;

        let isSomeoneVisible = false;

        const $directChildren = $group.querySelectorAll(':scope > [data-user-prop]');

        for(const $child of $directChildren) {
            const isChildVisible = isFallbackVisible($child as HTMLElement);
            if(isChildVisible){
                isSomeoneVisible = true;
                break;
            }
        }

        if(!isSomeoneVisible) {
            $fallback.classList.remove('hidden');
        }
    }
};